import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import accountRoutes from "./account";
import registerRoutes from "./register";
import { useMenuStore } from "@/stores/menu";
import { supportApiClient } from "@/lib/api.ts";
import { useSignupStore } from "@/stores/signup.ts";

export const URL_OFFER_SUMMARY =
  "https://support.simplybroadband.co.nz/offer-summary-overview";

const Home = () =>
  import(/* webpackChunkName: "home" */ "@/views/IndexPage.vue");
const FAQ = () => import(/* webpackChunkName: "home" */ "@/views/FAQ.vue");
const PrivacyPolicy = () =>
  import(/* webpackChunkName: "company" */ "@/views/PrivacyPolicy.vue");
const TermsAndConditions = () =>
  import(/* webpackChunkName: "company" */ "@/views/TermsAndConditions.vue");
const Login = () =>
  import(/* webpackChunkName: "login" */ "@/views/auth/LoginPage.vue");
const NotFound = () =>
  import(/* webpackChunkName: "not-found" */ "@/views/NotFound.vue");
const SpecialPage = () =>
  import(/* webpackChunkName: "special" */ "@/views/SpecialPage.vue");
const CustomerCare = () =>
  import(/* webpackChunkName: "not-found" */ "@/views/CustomerCare.vue");
const CustomerComplaints = () =>
  import(/* webpackChunkName: "not-found" */ "@/views/CustomerComplaints.vue");
// const OfferSummary = () =>
//   import(/* webpackChunkName: "not-found" */ "@/views/OfferSummary.vue");
const SpeedCalculator = () =>
  import(/* webpackChunkName: "not-found" */ "@/views/SpeedCalculator.vue");
const ContactUs = () =>
  import(/* webpackChunkName: "not-found" */ "@/views/ContactUs.vue");
const OurPlans = () =>
  import(/* webpackChunkName: "not-found" */ "@/views/OurPlans.vue");

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "home",
    component: Home,
    meta: {
      title: "Unlimited Internet  | Simply Broadband",
      description: "",
    },
  },
  {
    path: "/faq",
    name: "faq",
    component: FAQ,
    meta: {
      title: "Got questions? | Simply Broadband",
      description:
        "Find the answer to your questions on our FAQ page or get in touch with our team.",
    },
  },
  {
    path: "/privacy",
    name: "privacy",
    component: PrivacyPolicy,
    meta: {
      title: "Privacy Policy | Simply Broadband",
      description:
        "Learn more about our Privacy policy and how we treat and use your information.",
    },
  },
  {
    path: "/terms",
    name: "terms",
    component: TermsAndConditions,
    meta: {
      title: "Terms & Conditions | Simply Broadband",
      description: "Learn more about our Terms and Conditions.",
    },
  },
  {
    path: "/customer-care",
    name: "customer-care",
    component: CustomerCare,
    meta: {
      title: "Customer Care Policy | Simply Broadband",
      description:
        "This Customer Care Policy outlines our commitment to delivering the highest standards of service, support, and care to our valued customers.",
    },
  },
  {
    path: "/customer-complaints",
    name: "customer-complaints",
    component: CustomerComplaints,
    meta: {
      title: "Customer Complaints Policy | Simply Broadband",
      description: "Simply Broadband's Commitment to our customers",
    },
  },
  {
    path: "/offer-summary",
    name: "offer-summary",
    component: Home,
    beforeEnter() {
      window.location.href = URL_OFFER_SUMMARY;
    },
  },
  {
    path: "/speed-calculator",
    name: "speed-calculator",
    component: SpeedCalculator,
    meta: {
      title: "Internet speed calculator | Simply Broadband",
      description:
        "This calculator will help determine what you need in your household.",
    },
  },
  {
    path: "/contact-us",
    name: "contact-us",
    component: ContactUs,
    meta: {
      title: "Contact Us | Simply Broadband",
      description:
        "Being a New Zealand company, we work across New Zealand business hours and always happy to help",
    },
  },
  {
    path: "/our-plans",
    name: "our-plans",
    component: OurPlans,
    meta: {
      title: "Our Plans | Simply Broadband",
      description: "Enter your address to check if you have fibre connection",
    },
  },
  {
    path: "/login",
    name: "login",
    component: Login,
    meta: {
      title: "Login | Simply Broadband",
    },
  },
  ...registerRoutes,
  ...accountRoutes,
  {
    path: "/:catchAll(.*)",
    component: NotFound,
    props: (route) => ({
      page: route.meta.page,
    }),
    async beforeEnter(to) {
      const menuStore = useMenuStore();
      await menuStore.fetchConfig();
      const path = to.path.replace(/^\//, "");

      if (menuStore.aliases.includes(path)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await supportApiClient.get<any>("/v1/page", {
          params: {
            alias: path,
          },
        });
        const data = result.data;
        // Render SpecialPage in-place without redirecting, preserving the current URL
        to.matched[0].components!.default = SpecialPage;
        to.meta.page = data;
        to.meta.title = data.name + " | Simply Broadband";
      } else {
        to.matched[0].components!.default = NotFound;
        to.meta.title = "Page Not Found | Simply Broadband";
      }
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from) {
    const toRoutePreservesScroll =
      to.matched[to.matched.length - 1]?.meta?.preserveScrollPosition === true;
    const fromRoutePreservesScroll =
      from.matched[from.matched.length - 1]?.meta?.preserveScrollPosition ===
      true;

    // Keep the user's current position when switching tab-like nested routes.
    if (toRoutePreservesScroll && fromRoutePreservesScroll) {
      return false;
    }

    if (to.hash) {
      return {
        el: to.hash,
        top: 135,
        behavior: "smooth",
      };
    }

    return {
      left: 0,
      top: 0,
    };
  },
});

router.beforeEach((to, _from, next) => {
  // Capture utm_source from query params if present
  const utmSourceParam = to.query.utm_source;
  if (utmSourceParam && typeof utmSourceParam === "string") {
    const signupStore = useSignupStore();
    signupStore.utmSource.set(utmSourceParam);
  }

  // This goes through the matched routes from last to first, finding the closest route with a title.
  // e.g. if we have /some/deep/nested/route and /some, /deep, and /nested have titles, nested will be chosen.
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.title);
  const nearestWithDescription = to.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.description);

  const metaDescription = document.querySelector('meta[name="description"]');

  // Title
  document.title =
    (nearestWithTitle?.meta?.title as string) || "Simply Broadband";

  if (metaDescription) {
    if (nearestWithDescription) {
      metaDescription.setAttribute(
        "content",
        nearestWithDescription.meta.description as string,
      );
    } else {
      metaDescription.setAttribute("content", "");
    }
  }

  next();
});

export default router;
